import { Inject } from '@odinjs/odin';
import { BasicFile, ModalService, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterNavigate, OnMount, OnNavigate, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableNavigateEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { FragmentNames, PseudonymAppointmentsAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { CheckupSuggestionGroups, ScheduleItem } from '../../interfaces/schedules-suggestions';
import AppointmentService from '../../services/appointment-service';
import { closeToastNotification, DEFAULT_TIMEOUT, showToastNotificationError, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { AppointmentsSteps, CUSTOM_MESSAGE_ACTION, NO, REQUIREMENT_FIELDS, ScheduleAvailableType, ScheduleType, YES } from './pseudonym-types';
import { shallowCompare } from '@philips-internal/oswald-ui-utils';
import { FileUploadRequest, FileUploadResponse, HealthInsuranceCardProps, PseudonymCustomMenssage, UploadedFiles } from './pseudonym-interfaces';
import { LocaleTypes } from '../../constants/locale-types';
import { PatientHttpService } from '../../services/patient-http-service';
import React from 'react';
import RescheduleContext from '../../utils/reschedule-utils';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';

@Controller({ component: PseudonymAppointmentsAliases.LAYOUT_APPOINTMENTS, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS })
export default class PseudonymAppointmentsController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsAliases.WIZARD_APPOINTMENTS)
    wizardApi!: WizardAPI;

  private language = LanguageService.getCurrentLanguage();

  @OnMount({ target: PseudonymAppointmentsAliases.LAYOUT_APPOINTMENTS })
  onMountLayoutAppointments(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: ApplicationModulesIds.APPOINTMENTS } });
    dispatchEvent(event);
    const isReschedule = this.historyService.getCurrentRoute().includes(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
    this.setIsRescheduleContext(isReschedule);

    PseudonymAppointmentModel.clear();
  }

  @OnUnmount({ target: PseudonymAppointmentsAliases.LAYOUT_APPOINTMENTS })
  onUnMountLayoutAppointments(): void {
    PseudonymAppointmentModel.clear();
    ToastService.closeAll();
  }

  @OnMount({ target: PseudonymAppointmentsAliases.WIZARD_APPOINTMENTS })
  onMountWizardAppointments(event: MountEvent): void {
    if (event) {
      this.setWizardAppointmentsContext(this.wizardApi);
      this.hideInitialSteps(true);
    }
  }

  @OnNavigate({ target: PseudonymAppointmentsAliases.WIZARD_APPOINTMENTS })
  onNavigateWizardAppointments(event: AbortableNavigateEvent<string>): void {
    this.setFromToContext(event.from, event.to);
  }

  private setFromToContext(from: string, to: string | undefined): void {
    if (from === AppointmentsSteps.STEP_5_LOCATION
       && to != AppointmentsSteps.STEP_7_SUGGESTION && to != AppointmentsSteps.STEP_8_SUGGESTION_SUMMARY) {
      PseudonymAppointmentModel.stepSix.availableDays = [];
    }
  }

  @OnAfterNavigate({ target: PseudonymAppointmentsAliases.WIZARD_APPOINTMENTS })
  onAfterNavigateWizardAppointments(event: AbortableNavigateEvent<string>): void {
    this.skipWizardHiddenStep(event.from, event.to);
    this.onAfterNavigateWizardAppointmentsEventFrom(event.from);
    this.onAfterNavigateWizardAppointmentsEventTo(event.to);
    this.onAfterNavigateWizardAppointmentsEvent(event);
  }

  private onAfterNavigateWizardAppointmentsEventFrom(from: string): void {
    if (from === AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION) {
      PseudonymAppointmentModel.stepThree.saveAdditionalInformation();
    }
  }

  private onAfterNavigateWizardAppointmentsEventTo(to: string | undefined): void {
    const stepTo = to as AppointmentsSteps;
    if (!stepTo || this.isWizardStepHidden(stepTo)) {
      return;
    }

    switch (stepTo) {
      case AppointmentsSteps.STEP_2_SCHEDULING_ITEMS:
        this.setWizardStepDone(AppointmentsSteps.STEP_2_SCHEDULING_ITEMS, PseudonymAppointmentModel.stepTwo.procedures.length > 0 && PseudonymAppointmentModel.stepTwo.stepTwoOk);
        PseudonymAppointmentModel.stepTwo.clearProfessionalField();
        break;
      case AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION:
        PseudonymAppointmentModel.stepThree.loadAdditionalInformation();
        break;
      case AppointmentsSteps.STEP_5_LOCATION:
        this.onNavegateToLocationStep();
        break;
      case AppointmentsSteps.STEP_6_SCHEDULE_PERIOD:
        PseudonymAppointmentModel.stepSix.verifyAvailableCalendarDays();
        break;
      case AppointmentsSteps.STEP_8_SUGGESTION_SUMMARY:
        PseudonymAppointmentModel.stepEight.populateSummaryCardsWithSelectedItems();
        break;
    }
  }

  private onAfterNavigateWizardAppointmentsEvent(event: AbortableNavigateEvent<string>): void {
    const from = event.from;
    const to = event.to;
    const stepTo = to as AppointmentsSteps;
    const stepFrom = from as AppointmentsSteps;

    if (!stepTo || this.isWizardStepHidden(stepTo)) {
      return;
    }

    if (stepTo === AppointmentsSteps.STEP_4_QUIZ) {
      PseudonymAppointmentModel.stepFour.populateQuizTab()
        .then(() => {
          if (PseudonymAppointmentModel.hasMetadataQuiz) {
            this.skipWizardHiddenStep(from, to);
            return;
          }

          this.skipQuizStep(stepTo, stepFrom);
        });
    }
    if (stepTo === AppointmentsSteps.STEP_5_LOCATION || stepTo === AppointmentsSteps.STEP_4_QUIZ && stepFrom === AppointmentsSteps.STEP_7_SUGGESTION) {
      PseudonymAppointmentModel.integratedScheduleSequence = undefined as unknown as number;
    }

    if (stepTo === AppointmentsSteps.STEP_CHECKUP_SUGGESTION) {
      PseudonymAppointmentModel.stepCheckupSuggestions.populateCheckupSuggestion();
      this.setWizardStepDone(AppointmentsSteps.STEP_CHECKUP_SUGGESTION, false);
    }
    if (stepTo === AppointmentsSteps.STEP_7_SUGGESTION &&  ![AppointmentsSteps.STEP_8_SUGGESTION_SUMMARY, AppointmentsSteps.STEP_DOCUMENTS].includes(stepFrom)) {
      PseudonymAppointmentModel.waitingList.waitingListScheduled = 0;
      PseudonymAppointmentModel.stepSeven.populateSuggestionTab();
    }
    if (stepTo === AppointmentsSteps.STEP_7_SUGGESTION && stepFrom === AppointmentsSteps.STEP_8_SUGGESTION_SUMMARY) {
      PseudonymAppointmentModel.stepSeven.unSelectAllCards();
    }

    if (stepTo === AppointmentsSteps.STEP_DOCUMENTS) {
      PseudonymAppointmentModel.stepDocumentsUpload.populateFileUploaderItems()
        .then(() => {
          if (!PseudonymAppointmentModel.stepDocumentsUpload.hasFilesToUpload) {
            this.skipFilesStep(stepTo, stepFrom);
          }
        });
    }
  }

  private joinInfoUploadedFilesWithMemoryFiles(infoUploadedFiles: BasicFile[], memoryFiles: Map<string, Blob>): UploadedFiles[] {
    const uploadedFiles: UploadedFiles[] = [];
    infoUploadedFiles.forEach(element => {
      if (element && memoryFiles.has(element.filepath)){
        uploadedFiles.push({
          file: memoryFiles.get(element.filepath),
          info: element
        } as UploadedFiles);
      }
    });
    return uploadedFiles;
  }

  private updateInsuranceEligibility(insuranceSelected: HealthInsuranceCardProps): Promise<HttpResponse<void>>  {
    const body =  {
      sequenceCategoryVat: insuranceSelected.sequenceVat
    };
    return PatientHttpService.put(APPLICATION_ENDPOINTS.UPDATE_INSURANCE_CARD, { cardId: insuranceSelected.cardId }, { body });
  }

  private localeSubmitProcessVat(insuranceSelected: HealthInsuranceCardProps): void {
    if (this.language === LocaleTypes.ARGENTINA) {
      this.updateInsuranceEligibility(insuranceSelected);
    }
  }

  @OnSubmit({ target: PseudonymAppointmentsAliases.WIZARD_APPOINTMENTS })
  async handleOnSubmitWizardAppointments(event: SubmitEvent): Promise<void> {
    if (!event) {
      return;
    }
    const insuranceSelected = PseudonymAppointmentModel.stepOne.healthInsurance;
    const panelLoaderConfirmationApi = PseudonymAppointmentModel.stepEight.panelLoaderConfirmationApi;
    const suggestionList = PseudonymAppointmentModel.stepSeven.suggestionSelected[0].suggestionItems;
    const waitingListData = PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray;
    const confirmAppointmentInfoList: Array<Record<string, Value>> = [];
    const externalPhysicianInfoList: Array<Record<string, Value>> = [];

    if (PseudonymAppointmentModel.stepTwo.hasCheckup) {
      this.checkupConfirmation();
      return;
    }

    this.localeSubmitProcessVat(insuranceSelected);
    PseudonymAppointmentModel.stepTwo.procedures.forEach(procedure => {
      externalPhysicianInfoList.push({
        externalPhysicianMDLicenseNumber: procedure.externalPhysicianInfo.externalPhysicianLicenseNumber,
        externalPhysicianMDLicenseState: procedure.externalPhysicianInfo.externalPhysicianLicenseState,
        externalPhysicianName: procedure.externalPhysicianInfo.externalPhysicianName,
        nrSeqAgeInt: PseudonymAppointmentModel.integratedScheduleSequence
      })
    });

    const sameExternalPhysician = PseudonymAppointmentModel.stepTwo.procedures.every((procedure, _index, array) => !shallowCompare(procedure.externalPhysicianInfo, array[0].externalPhysicianInfo, ['externalPhysicianName', 'externalPhysicianMDLicenseNumber', 'externalPhysicianMDLicenseState']));

    panelLoaderConfirmationApi.setVisible(true);
    this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);

    if (suggestionList?.length > 0) {
      suggestionList.forEach((suggestion, index) => {
        const waitingListItem = waitingListData.find(item => item.nrSeqAgeintItem === suggestion.nrSeqAgeintItem);
        externalPhysicianInfoList[index].nrSeqAgeIntItem = suggestion.nrSeqAgeintItem;

        if (!suggestion.suggestionTypeConsultation && suggestion.teleconsultationType) {
          suggestion.suggestionTypeConsultation = suggestion.teleconsultationType;
        }

        if (!waitingListItem && PseudonymAppointmentModel.stepSeven.suggestionSelected[0].hasSchedulingTimes) {
          const confirmAppointmentInfo = this.mountConfirmAppointmentInfo(suggestion);
          confirmAppointmentInfoList.push(confirmAppointmentInfo);
        }
      });
    }

    const memoryFiles = PseudonymAppointmentModel.stepDocumentsUpload.memoryFiles;
    const infoUploadedFiles = PseudonymAppointmentModel.stepDocumentsUpload.uploadedFiles;
    const uploadDocumentsRequestPromises: Array<Promise<FileUploadRequest>> = [];
    const uploadedFiles = this.joinInfoUploadedFilesWithMemoryFiles(infoUploadedFiles, memoryFiles);

    uploadedFiles.forEach(uploaded => {
      const formData = new FormData();
      formData.append('file', uploaded.file);

      const uploadedFilePromise = HttpService.post(APPLICATION_ENDPOINTS.UPLOAD_APPOINTMENT_DOCUMENTS, {}, { body: formData })
        .then(uploadedFilesResponse => {
          const fileUpload = uploadedFilesResponse.body as FileUploadResponse;
          const fileUploadRequest: FileUploadRequest = {
            fileName: fileUpload.fileName,
            path: fileUpload.path,
            sequenceNumberFileRule: Number(uploaded.info.predefinedFileKey),
          }
          return fileUploadRequest;
        });

      uploadDocumentsRequestPromises.push(uploadedFilePromise);
    });
    Promise.all(uploadDocumentsRequestPromises).then(filesResponse => {
      HttpService.post(APPLICATION_ENDPOINTS.CREATE_APPOINTMENT_DOCUMENTS,
        { integratedScheduleId: PseudonymAppointmentModel.integratedScheduleSequence },
        { body: filesResponse as unknown as Record<string, Value> });
    });

    AppointmentService.confirmAndRequestAppointments(PseudonymAppointmentModel.integratedScheduleSequence, PseudonymAppointmentModel.stepOne.currentPatientSelected.id, confirmAppointmentInfoList)
      .then(response => {
        externalPhysicianInfoList.forEach(item => {
          if (!item.externalPhysicianMDLicenseNumber || !item.externalPhysicianMDLicenseState) return;
          HttpService.post(APPLICATION_ENDPOINTS.ADD_PHYSICIAN_NAME_TO_SCHEDULE, {}, {body: {
            ieSamePhysician: sameExternalPhysician? YES : NO,
            integratedScheduleSequence: sameExternalPhysician ? item.nrSeqAgeInt : item.nrSeqAgeIntItem,
            ...item
          }});
        });
        this.openConfirmation(response);
        PseudonymAppointmentModel.stepSix.availableDays = [];
      }).catch(() => {
        this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, true);
        showToastNotificationError(MessageService.translate('patient.appointments.confirm-failed'));
      }).finally(() => {
        panelLoaderConfirmationApi.setVisible(false);
      });
  }

  async openConfirmation(response: Record<string, Value> | Record<string, Value>[]): Promise<void> {
    if(PseudonymAppointmentModel.stepTwo.hasCheckup) {
      return this.openDetailConfirmation(response);
    }

    const price = Number(PseudonymAppointmentModel.stepSeven.suggestions.suggestionList[0].priceNoFormatted);
    const payment = PseudonymAppointmentModel.stepEight.paymentConfiguration;

    if(price > 0 && payment === 'S') {
      return this.openConfirmationDialog(response);
    }

    return this.openDetailConfirmation(response);
  }

  async openConfirmationDialog(response: Record<string, Value> | Record<string, Value>[]): Promise<void> {
    let defaultMessage = 'patient.online-payment.description';

    await HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE, { action: CUSTOM_MESSAGE_ACTION.ONLINE_PAYMENT }).then(customMessageResponse => {
      const customMessage = customMessageResponse.body as PseudonymCustomMenssage;
      defaultMessage = customMessage?.message ?? defaultMessage;
    });

    ModalService.openDialog({
      closeable: false,
      dialogDescription: defaultMessage,
      dockButtons: [
        {
          action: () => {
            this.openDetailConfirmation(response)
          },
          title: 'patient.online-payment.pay-after',
          type: ModalButtonType.CANCEL,
        },
        {
          action: () => {
            this.openPaymentsModal(response)
          },
          title: 'patient.online-payment.pay-now',
          type: ModalButtonType.SUCCESS,
        }
      ],
      title: 'patient.online-payment',
    });
  }

  private openPaymentsModal(response: Record<string, Value> | Record<string, Value>[]): void {
    ModalService.openFullscreen({
      className: 'schedule-payment-modal-custom-bg',
      closeable: true,
      fragment: FragmentNames.MODAL_PAYMENT_IFRAME,
      onClose: () => this.openDetailConfirmation(response),
      title: 'patient.online-payment',
    });
  }

  private openDetailConfirmation(response: Record<string, Value> | Record<string, Value>[]): void {
    ModalService.openFullscreen({
      className: 'schedule-confirmation-modal-custom-bg',
      closeable: true,
      contextData: { data: response },
      dockButtons: [],
      fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_CONFIRMATION,
      onClose: () => this.historyService.push(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS, {}, { scrollToTop: true }),
      title: MessageService.translate('patient.schedule-success'),
    });
  }

  public checkupConfirmation(): void {
    let endpoint = '';
    const body = {
      checkupRequisitionParameters: PseudonymAppointmentModel.stepCheckupSuggestions.checkupRequisitionParameters,
      checkupTimeItem: PseudonymAppointmentModel.stepCheckupSuggestions.selectedSuggestion,
    };

    if (this.historyService.getCurrentRoute().includes(APPLICATION_ROUTES.RESCHEDULE_CHECKUP)) {
      endpoint = APPLICATION_ENDPOINTS.POST_RESCHEDULE_CHECKUP;
      this.rescheduleCheckupRequisitionParameters();
    } else {
      endpoint = APPLICATION_ENDPOINTS.POST_CONFIRM_CHECKUP;
    }

    HttpService.post<CheckupSuggestionGroups>(endpoint, {}, { body }).then(response => {
      const checkupSuggestionGroups = response.body as CheckupSuggestionGroups;
      const message = checkupSuggestionGroups.message?.find(record => record.message);

      if (message && message.code > 0) {
        const id = showToastNotificationWarning(MessageService.translate(message.message), DEFAULT_TIMEOUT.NONE, false, '',
          <button onClick={ () => this.returnStepAfterAction(id) }> { MessageService.translate('tws.modal.btn-back') } </button>);
        return;
      }

      if (message && message.code < 0) {
        showToastNotificationError(MessageService.translate(message.message));
        return;
      }

      const confirmedSchedule = [{
        ieStatus: ScheduleAvailableType.REQUEST,
        ieTipoAgendamento: ScheduleType.CHECKUP,
        nrSeqAgeintItem: 0,
        nrSequencia: checkupSuggestionGroups.checkupTimeItemList[0].sequence,
      }] as Record<string, Value>[];

      AppointmentService.confirmAndRequestAppointments(checkupSuggestionGroups.checkupTimeItemList[0].sequence, PseudonymAppointmentModel.stepOne.currentPatientSelected.id, confirmedSchedule);
      this.openConfirmation(confirmedSchedule);
    });
  }

  private returnStepAfterAction(toastId: string): void {
    this.setPrevStep();
    closeToastNotification(toastId);
  }

  private mountConfirmAppointmentInfo(suggestion: ScheduleItem): Record<string, Value> {
    const confirmAppointmentInfo = {
      attachedFileList: suggestion.attachedPrescheduleFiles,
      availableType: suggestion.availableType,
      cdAgenda: suggestion.agendaId,
      cdEspecialidade: suggestion.cdEspecialidade,
      cdEstabelecimento: suggestion.cdEstabelecimento,
      cdMedico: suggestion.cdMedico && suggestion.cdMedico.length > 0 ? suggestion.cdMedico : null,
      cdPessoaFisica: String(PseudonymAppointmentModel.stepOne.currentPatientSelected.id),
      dsNota: suggestion.observation,
      dtAgenda: suggestion.selectedDate || suggestion.date,
      ieTipoAgendamento: suggestion.type,
      nmPreferredTime: suggestion.period,
      nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
      nrSeqAgenda: suggestion.nrSeqAgenda,
      nrSeqProcInterno: suggestion.nrSeqProcInterno,
      quantityClassification : suggestion.quantityClassification,
      quantityClassificationTelehealth : suggestion.quantityClassificationTelehealth,
      ruleClassification : suggestion.ruleClassification,
      ruleClassificationTelehealth : suggestion.ruleClassificationTelehealth,
      suggestionTypeConsultation: suggestion.suggestionTypeConsultation
    } as Record<string, Value>;

    return confirmAppointmentInfo;
  }

  private skipQuizStep(stepTo: AppointmentsSteps, stepFrom: AppointmentsSteps): void {
    this.setWizardStepDone(stepTo, true);

    const arrayStepNext = [AppointmentsSteps.STEP_5_LOCATION, AppointmentsSteps.STEP_7_SUGGESTION];
    const arrayStepPrev = [AppointmentsSteps.STEP_2_SCHEDULING_ITEMS, AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION];

    if (arrayStepNext.find(element => stepFrom === element)) {
      this.setPrevStep();
    }

    if (arrayStepPrev.find(element => stepFrom === element)) {
      this.setNextStep();
    }
  }

  private skipFilesStep(stepTo: AppointmentsSteps, stepFrom: AppointmentsSteps): void {
    this.setWizardStepDone(stepTo, true);

    const arrayStepNext = [AppointmentsSteps.STEP_8_SUGGESTION_SUMMARY];
    const arrayStepPrev = [AppointmentsSteps.STEP_2_SCHEDULING_ITEMS, AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION, AppointmentsSteps.STEP_4_QUIZ, AppointmentsSteps.STEP_5_LOCATION, AppointmentsSteps.STEP_7_SUGGESTION];

    if (arrayStepNext.find(element => stepFrom === element)) {
      this.setPrevStep();
    }

    if (arrayStepPrev.find(element => stepFrom === element)) {
      this.setNextStep();
    }
  }

  private onNavegateToLocationStep(): void {
    const applicationContext = getFoundationDataContext();
    const isLocationRequired = applicationContext.get(REQUIREMENT_FIELDS.LOCATION_REQUIRED);
    const isLocationStepDone = applicationContext.get(REQUIREMENT_FIELDS.LOCATION_STEP_DONE);
    this.wizardApi.setStepDone(AppointmentsSteps.STEP_5_LOCATION, (isLocationStepDone || !isLocationRequired));
    PseudonymAppointmentModel.stepFive.loadLocation();
  }

  private rescheduleCheckupRequisitionParameters(): void {
    PseudonymAppointmentModel.stepCheckupSuggestions.checkupRequisitionParameters.checkupOldSequence = RescheduleContext.rescheduleCheckup.checkupCode;
    PseudonymAppointmentModel.stepCheckupSuggestions.checkupRequisitionParameters.sector = RescheduleContext.rescheduleCheckup.sectorCode;
  }
}
